const MIME_BY_EXT = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  html: "text/html",
  htm: "text/html",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
};

export const MAX_DOCS = 2;

export const MAX_PER_FILE_BYTES_DEFAULT = 10 * 1024 * 1024; // 10 MB
export const MAX_PER_FILE_BYTES_TEXT = 2 * 1024 * 1024; // 2 MB
export const MAX_PER_FILE_BYTES_IMAGE = 5 * 1024 * 1024; // 5 MB
export const MAX_TOTAL_BYTES = 10 * 1024 * 1024; // 10 MB total

export const ALLOWED_MIME = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);

export const WEB_ACCEPT_ATTR =
  ".pdf,.docx,.pptx,text/*,.txt,.md,.csv,.html,image/*";

export const MOBILE_PICKER_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/*",
  "image/*",
];

const extensionFromName = (name = "") => {
  const clean = String(name || "").trim().toLowerCase();
  if (!clean.includes(".")) return "";
  return clean.slice(clean.lastIndexOf(".") + 1);
};

export const inferMimeTypeFromName = (name = "") => MIME_BY_EXT[extensionFromName(name)] || "";

export const normalizeUploadMimeType = (mime = "", name = "") => {
  const normalized = String(mime || "").trim().toLowerCase();
  if (normalized) return normalized;
  return inferMimeTypeFromName(name);
};

export const isAllowedMime = (t = "") => {
  const normalized = String(t || "").trim().toLowerCase();
  return ALLOWED_MIME.has(normalized) || normalized.startsWith("text/");
};

export const perFileCap = (t = "") => {
  const normalized = String(t || "").trim().toLowerCase();
  if (normalized.startsWith("text/")) return MAX_PER_FILE_BYTES_TEXT;
  if (normalized.startsWith("image/")) return MAX_PER_FILE_BYTES_IMAGE;
  return MAX_PER_FILE_BYTES_DEFAULT;
};

export const bytesToReadable = (n = 0) => {
  const value = Number(n || 0);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${(value / 1024).toFixed(2)} KB`;
};

export const fileFingerprint = (file = {}) => {
  const name = String(file?.name || "");
  const size = Number(file?.size || 0);
  const type = normalizeUploadMimeType(file?.type, name);
  const lastModified = Number(file?.lastModified || 0);
  const uri = String(file?.uri || "");
  return `${name}|${size}|${type}|${lastModified}|${uri}`;
};

export const validateIncomingDocuments = ({
  existingFiles = [],
  incomingFiles = [],
} = {}) => {
  const errors = [];
  const acceptedFiles = [];
  const existing = Array.isArray(existingFiles) ? existingFiles : [];
  const incoming = Array.isArray(incomingFiles) ? incomingFiles : [];

  const remainingSlots = Math.max(0, MAX_DOCS - existing.length);
  if (remainingSlots <= 0) {
    return {
      acceptedFiles,
      errors: [`You already attached ${MAX_DOCS} documents. Remove one to add another.`],
    };
  }

  const selected = incoming.slice(0, remainingSlots);
  const existingFingerprints = new Set(existing.map(fileFingerprint));

  for (const rawFile of selected) {
    const name = String(rawFile?.name || "document");
    const type = normalizeUploadMimeType(rawFile?.type, rawFile?.name);
    const size = Number(rawFile?.size || 0);
    const file = {
      ...rawFile,
      uri: rawFile?.uri,
      name,
      type,
      size,
      lastModified: rawFile?.lastModified,
    };

    if (existingFingerprints.has(fileFingerprint(file))) {
      errors.push(`"${file.name}" is already attached.`);
      continue;
    }

    if (!isAllowedMime(type)) {
      errors.push(`Unsupported file type for "${file.name}" (${type || "unknown"}).`);
      continue;
    }

    const cap = perFileCap(type);
    if (size > cap) {
      errors.push(
        `"${file.name}" exceeds per-file limit of ${bytesToReadable(cap)} (size: ${bytesToReadable(size)}).`
      );
      continue;
    }

    acceptedFiles.push(file);
  }

  const totalExisting = existing.reduce((sum, file) => sum + Number(file?.size || 0), 0);
  const totalAccepted = acceptedFiles.reduce((sum, file) => sum + Number(file?.size || 0), 0);
  if (totalExisting + totalAccepted > MAX_TOTAL_BYTES) {
    const remaining = Math.max(0, MAX_TOTAL_BYTES - totalExisting);
    errors.push(
      `Total upload exceeds ${bytesToReadable(MAX_TOTAL_BYTES)}. You can add at most ${bytesToReadable(remaining)} more.`
    );
    return { acceptedFiles: [], errors };
  }

  return { acceptedFiles, errors };
};
